import { useCallback, useEffect, useRef } from 'react';
import { usePanelStore, constrainPanelWidth } from './createPanelStore';
import type { PanelStoreState } from './types';

export interface UseResizablePanelOptions {
  /**
   * Custom CSS variable name (default: '--panel-width')
   */
  cssVariable?: string;
  /**
   * Custom store instance (optional, defaults to shared store)
   */
  store?: PanelStoreState;
}

export function useResizablePanel(options: UseResizablePanelOptions = {}) {
  const { cssVariable = '--panel-width', store } = options;

  // Use custom store or default shared store
  const defaultStore = usePanelStore();
  const storeData = store || defaultStore;
  const { width, isDragging, setWidth, setDragging } = storeData;

  const rafIdRef = useRef<number | null>(null);
  const currentWidthRef = useRef<number>(width);

  const handleMouseDown = useCallback(() => {
    setDragging(true);
    currentWidthRef.current = width;
  }, [setDragging, width]);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Use RAF for smooth updates
      rafIdRef.current = requestAnimationFrame(() => {
        const viewportWidth = window.innerWidth;
        const newWidth = viewportWidth - e.clientX;
        const constrainedWidth = constrainPanelWidth(newWidth, viewportWidth);

        // Update CSS variable directly (no React re-renders)
        document.documentElement.style.setProperty(
          cssVariable,
          `${constrainedWidth}px`
        );
        currentWidthRef.current = constrainedWidth;
      });
    },
    [isDragging, cssVariable]
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);

    // Save final width to Zustand (persisted to localStorage)
    if (currentWidthRef.current !== width) {
      setWidth(currentWidthRef.current);
    }

    // Clean up RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, [setDragging, setWidth, width]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Prevent text selection during drag
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      // Set initial CSS variable
      document.documentElement.style.setProperty(cssVariable, `${width}px`);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, width, cssVariable]);

  // Initialize CSS variable on mount and when width changes (from localStorage)
  useEffect(() => {
    document.documentElement.style.setProperty(cssVariable, `${width}px`);
  }, [width, cssVariable]);

  return {
    width,
    isDragging,
    handleMouseDown,
  };
}
